import axios from "axios";
import { IMsalContext } from "@azure/msal-react";
import { getAccessToken } from "config/token";
import { NEXUS_API_BASE_URL, NEXUS_API_BASE_URL_V2 } from "const";
import {
  ActiveTransfersResponse,
  CancelTransferResult,
  CreateTransferPayload,
  CreateTransferResult,
  DeleteScheduleResult,
  ListTransfersParams,
  PastTransfersResponse,
} from "./types";

// ── Transfer spaces ───────────────────────────────────────────────────────────

export const EXTERNAL_DATA_EXCHANGE_DOMAIN = "External Data Exchange";

export interface SourceSpace {
  name: string; // space_name — sent to API
  display_name: string;
  bucket_name: string; // S3 bucket id — used to build prefix URIs
  domain_name?: string;
}

export interface FetchTransferSpacesOptions {
  searchTerm?: string;
  region?: string | null;
  /**
   * Domain filter. When omitted/null/"all", fetches owned spaces for every
   * known domain (API requires a concrete domain_name per request).
   */
  domainName?: string | null;
}

/** Bearer token helper */
const bearer = async (msalInstance: IMsalContext) => {
  const token = await getAccessToken(msalInstance);
  return "Bearer " + (token ?? { token: "" }).token;
};

interface DomainResolution {
  domains: string[];
  auth?: string;
}

/**
 * Resolve active domain display names from localStorage cache, falling back to get-domains API.
 */
const resolveDomainNames = async (
  msalInstance: IMsalContext,
): Promise<DomainResolution> => {
  let domains: { display_name?: string; active?: string }[] = [];
  let auth: string | undefined;
  const cached = localStorage.getItem("domainsData");
  if (cached) {
    try {
      const parsed = JSON.parse(cached);
      if (Array.isArray(parsed)) domains = parsed;
    } catch {
      domains = [];
    }
  }

  if (!domains.length) {
    auth = await bearer(msalInstance);
    const res = await axios.get(`${NEXUS_API_BASE_URL}/nexus/get-domains`, {
      headers: { Authorization: auth },
    });
    domains = Array.isArray(res.data) ? res.data : [];
    if (domains.length) {
      localStorage.setItem("domainsData", JSON.stringify(domains));
    }
  }

  return {
    domains: domains
      .filter(d => d.active === "1")
      .map(d => d.display_name?.trim() || "")
      .filter(name => name && name !== "data_domains"),
    auth,
  };
};

/**
 * Fetch owned spaces for a single concrete domain (API requires domain_name).
 */
const fetchOwnedSpacesForDomain = async (
  auth: string,
  domainName: string,
  searchTerm?: string,
  region?: string | null,
): Promise<SourceSpace[]> => {
  const allSpaces: SourceSpace[] = [];
  let page = 1;
  const regionName = region?.trim() || "all";

  while (true) {
    const params = new URLSearchParams({
      pagination_needed: "true",
      scope: "owned",
      region_name: regionName,
      domain_name: domainName,
      page: String(page),
      page_size: "100",
    });
    if (searchTerm?.trim())
      params.append("search_space_name", searchTerm.trim());

    const res = await axios.get(
      `${NEXUS_API_BASE_URL_V2}/nexus/spaces?${params}`,
      {
        headers: { Authorization: auth },
      },
    );

    const raw: any[] =
      res.data?.spaces ??
      res.data?.buckets ??
      (Array.isArray(res.data) ? res.data : []);

    allSpaces.push(
      ...raw.map((s: any) => ({
        name: s.space_name ?? s.name ?? "",
        display_name: s.space_name ?? s.name ?? "",
        bucket_name: s.bucket_name ?? s.name ?? "",
        domain_name:
          s.domain_name ?? s.metadata?.["primary-data-domain"] ?? domainName,
      })),
    );

    if (!res.data?.pagination?.has_next) break;
    page++;
  }

  return allSpaces;
};

/**
 * Fetch spaces the logged-in user owns, filtered by region and domain.
 * Optionally filtered by a search term (matched server-side).
 * When domainName is omitted/null/"all", fans out one request per active domain
 * and merges results (backend does not support domain_name=all).
 */
export const fetchTransferSpaces = async (
  msalInstance: IMsalContext,
  options: FetchTransferSpacesOptions = {},
): Promise<SourceSpace[]> => {
  const { searchTerm, region, domainName } = options;
  const domainFilter = domainName?.trim();

  if (domainFilter && domainFilter !== "all") {
    const auth = await bearer(msalInstance);
    return fetchOwnedSpacesForDomain(auth, domainFilter, searchTerm, region);
  }

  const { domains, auth: domainLookupAuth } = await resolveDomainNames(
    msalInstance,
  );
  if (!domains.length) return [];

  const auth = domainLookupAuth ?? (await bearer(msalInstance));

  const perDomain = await Promise.all(
    domains.map(async dn => {
      try {
        return await fetchOwnedSpacesForDomain(auth, dn, searchTerm, region);
      } catch {
        // One domain failing should not block the rest of the picker.
        return [];
      }
    }),
  );

  // Dedupe by space name (keep first) in case a space appears under multiple domains.
  const seen = new Set<string>();
  const merged: SourceSpace[] = [];
  for (const space of perDomain.flat()) {
    if (!space.name || seen.has(space.name)) continue;
    seen.add(space.name);
    merged.push(space);
  }
  return merged;
};

/**
 * Fetch External Data Exchange spaces the user owns (same region as opened space).
 * Used as the source picker when transferring into a non-EDX space.
 */
export const fetchSourceSpaces = async (
  msalInstance: IMsalContext,
  searchTerm?: string,
  region?: string | null,
): Promise<SourceSpace[]> =>
  fetchTransferSpaces(msalInstance, {
    searchTerm,
    region,
    domainName: EXTERNAL_DATA_EXCHANGE_DOMAIN,
  });

const DEFAULT_PAGE_LIMIT = 20;

const authHeaders = (auth: string) => ({ Authorization: auth });

const jsonHeaders = (auth: string) => ({
  ...authHeaders(auth),
  "Content-Type": "application/json",
});

/** Cursor query for Active and Past. `limit` applies to `items` only. */
const buildTransferQuery = (space: string, params?: ListTransfersParams) => {
  const requested = params?.limit ?? DEFAULT_PAGE_LIMIT;
  const limit = Math.min(100, Math.max(1, requested));
  const search = new URLSearchParams({
    space,
    limit: String(limit),
  });
  if (params?.cursor) search.set("cursor", params.cursor);
  return search;
};

export const fetchActiveTransfers = async (
  msalInstance: IMsalContext,
  space: string,
  params?: ListTransfersParams,
): Promise<ActiveTransfersResponse> => {
  const auth = await bearer(msalInstance);
  const query = buildTransferQuery(space, params);
  const res = await axios.get(
    `${NEXUS_API_BASE_URL}/data-transfers/active?${query}`,
    { headers: authHeaders(auth) },
  );
  return res.data as ActiveTransfersResponse;
};

export const fetchPastTransfers = async (
  msalInstance: IMsalContext,
  space: string,
  params?: ListTransfersParams,
): Promise<PastTransfersResponse> => {
  const auth = await bearer(msalInstance);
  const query = buildTransferQuery(space, params);
  const res = await axios.get(
    `${NEXUS_API_BASE_URL}/data-transfers/past?${query}`,
    { headers: authHeaders(auth) },
  );
  return res.data as PastTransfersResponse;
};

export const createTransfer = async (
  msalInstance: IMsalContext,
  payload: CreateTransferPayload,
): Promise<CreateTransferResult> => {
  const auth = await bearer(msalInstance);
  const res = await axios.post(
    `${NEXUS_API_BASE_URL}/data-transfers/create`,
    payload,
    { headers: jsonHeaders(auth) },
  );
  return res.data as CreateTransferResult;
};

/** Stop a running Glue execution. Does not delete a schedule. */
export const cancelTransfer = async (
  msalInstance: IMsalContext,
  executionId: string,
  space: string,
): Promise<CancelTransferResult> => {
  const auth = await bearer(msalInstance);
  const res = await axios.post(
    `${NEXUS_API_BASE_URL}/data-transfers/cancel`,
    { execution_id: executionId, space },
    { headers: jsonHeaders(auth) },
  );
  return res.data as CancelTransferResult;
};

/** Delete a live EventBridge schedule. */
export const deleteSchedule = async (
  msalInstance: IMsalContext,
  space: string,
  scheduleId: string,
): Promise<DeleteScheduleResult> => {
  const auth = await bearer(msalInstance);
  const query = new URLSearchParams({ space, schedule_id: scheduleId });
  const res = await axios.delete(
    `${NEXUS_API_BASE_URL}/data-transfers/schedules?${query}`,
    { headers: authHeaders(auth) },
  );
  return res.data as DeleteScheduleResult;
};
